/**
 * ageConsentInterceptor — the GLOBAL backbone that makes every COPPA-gated
 * route degrade gracefully instead of throwing a raw 426/403 at the user.
 *
 * Installed ONCE on the shared axios instance (App.js, alongside the 401
 * interceptor). Two behaviors:
 *
 *  1. 426 `age_required` (an existing NULL-DOB user hits a gated action) →
 *     open the neutral DOB-capture modal, await the user's entry (the modal
 *     POSTs /auth/age), then TRANSPARENTLY RETRY the original request and
 *     resolve the original promise with the retry's result. The caller never
 *     sees the 426 — its `await api.foo()` simply takes a beat longer and then
 *     returns the data, post-capture. This is what makes the age gate
 *     invisible to all ~dozen gated surfaces without per-surface wiring.
 *
 *  2. 403 `consent_required` (an under-13 without `platform_minor` consent
 *     hits an open-section egress) → raise the limited-mode parent-invite
 *     overlay, then REJECT the original promise (we do NOT retry — the unfreeze
 *     needs a guardian action that may take days). The calling surface still
 *     gets its rejection so it can render its own inline limited state; the
 *     overlay is the shared "ask a parent" affordance over the top.
 *
 * Everything else passes through untouched (the 401 interceptor + axiosRetry
 * own their concerns). The Career composite-gate 403 is deliberately NOT
 * handled here — it has a generic FORBIDDEN code and a rich dedicated
 * student/teacher flow in career/, so CareerSection classifies + routes it.
 *
 * LOOP SAFETY: a retried request is tagged `_coppaAgeRetried` on its config so
 * a SECOND 426 (the DOB still didn't satisfy the gate — e.g. a true under-13 on
 * a >=13-only route, which shouldn't occur but must not loop) is NOT retried
 * again; it propagates. BACKGROUND requests (`x-background-request`) are
 * skipped entirely — a prefetch must never pop a modal in the user's face.
 *
 * GATE UNMOUNTED MID-QUESTION: a request the capture is HOLDING when the gate
 * asking it unmounts (signing out takes the whole signed-in tree, the page
 * that sent the request with it) is CANCELLED, not failed with its 426. A page
 * answers a 426 with its age/consent handling, and that handling can write:
 * CareerAdvisor keeps the student's question in sessionStorage for their
 * return. Run after a logout, that write lands after the logout's wipe, for
 * whoever signs in to the tab next. A cancellation carries no status, so no
 * page's 426 handling runs. CareerAdvisor does not check axios.isCancel: it
 * stands down only because classifyCareerError maps an error with no status
 * to 'error', whose branch adds a note to a chat that is gone and writes
 * nothing. A classifier that sent a status-less error to 'consent' would
 * reopen the stash-after-wipe leak (CareerAdvisor.test.js, "signing out with
 * the age prompt open", fails if it does). Pages that check axios.isCancel
 * stand down on that.
 *
 * Only held requests: a 426 that ARRIVES after the gate unmounted finds no
 * gate at all (requestDob()'s 'age-gate-unavailable', as during onboarding)
 * and still surfaces as the raw 426, to a page that is gone, until the
 * session-epoch fix (brief 039).
 */
import { CanceledError } from 'axios';
import {
  isAgeGateUnmounted,
  requestDob,
  showPlatformConsent,
} from './ageConsentController';
import { isAgeRequired, isPlatformConsentRequired } from './coppaApi';

const RETRY_FLAG = '_coppaAgeRetried';

/** True if the request was tagged a background/prefetch call (skip the UI). */
function isBackground(config) {
  const h = config?.headers || {};
  return (
    h['x-background-request'] === 'true' || h['X-Background-Request'] === 'true'
  );
}

/**
 * Install the COPPA age/consent response interceptor on `axiosInstance`.
 * Returns the interceptor id so the caller can eject it (App.js ejects on
 * unmount, mirroring the 401 interceptor). `deps` lets tests inject fakes for
 * the controller calls without monkey-patching the module.
 */
export function installAgeConsentInterceptor(axiosInstance, deps = {}) {
  const onAge = deps.requestDob || requestDob;
  const onConsent = deps.showPlatformConsent || showPlatformConsent;

  return axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;

      // Skip background/prefetch requests — never interrupt the user for a
      // fire-and-forget call. Let the rejection flow through silently.
      if (!config || isBackground(config)) {
        return Promise.reject(error);
      }

      // ── 426 age_required → capture DOB, then retry the original request.
      if (isAgeRequired(error) && !config[RETRY_FLAG]) {
        try {
          // Await the user's DOB entry (single-flight: concurrent 426s share
          // one modal). The modal POSTs /auth/age; on success this resolves.
          await onAge();
        } catch (reason) {
          // The gate unmounted with the question open: its page went with it
          // (see the header) → cancel, so no page-level 426 handling runs.
          if (isAgeGateUnmounted(reason)) {
            return Promise.reject(
              new CanceledError('age-gate-unmounted', config, error.request),
            );
          }
          // User dismissed the capture, or no gate was mounted when the 426
          // arrived (onboarding; or after a logout, where the page is gone
          // too: see the header) → surface the original 426 so the calling
          // surface can render its own state.
          return Promise.reject(error);
        }
        // Re-issue the ORIGINAL request, tagged so a repeat 426 can't loop.
        const retryConfig = { ...config, [RETRY_FLAG]: true };
        return axiosInstance(retryConfig);
      }

      // ── 403 consent_required → raise the limited-mode parent-invite overlay,
      // then reject (no retry — the unfreeze is a guardian action).
      if (isPlatformConsentRequired(error)) {
        onConsent();
        return Promise.reject(error);
      }

      // Everything else: not ours.
      return Promise.reject(error);
    },
  );
}
